import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";

interface SpeakerApplicationDialogProps {
  eventId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const SpeakerApplicationDialog = ({
  eventId,
  open,
  onOpenChange
}: SpeakerApplicationDialogProps) => {
  const { toast } = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    company: "",
    jobTitle: "",
    bio: "",
    topicTitle: "",
    topicDescription: "",
    linkedinUrl: "",
    portfolioUrl: ""
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.fullName || !formData.email || !formData.bio || !formData.topicTitle || !formData.topicDescription) {
      toast({
        title: "Missing Information",
        description: "Please fill in all required fields (Name, Email, Bio, Talk Title, and Talk Description)",
        variant: "destructive"
      });
      return;
    }

    setSubmitting(true);

    try {
      // Optional: include user_id if signed in, otherwise submit anonymously
      const { data: { user } } = await supabase.auth.getUser();

      const { error } = await supabase
        .from('speaker_applications')
        .insert({
          event_id: eventId,
          full_name: formData.fullName,
          email: formData.email,
          speaker_bio: formData.bio,
          topic_title: formData.topicTitle,
          topic_description: formData.topicDescription,
          linkedin_url: formData.linkedinUrl || null,
          portfolio_url: formData.portfolioUrl || null,
          user_id: user?.id ?? null,
          status: 'pending',
          experience_level: 'intermediate',
          presentation_format: 'talk'
        });

      if (error) throw error;

      toast({
        title: "Application Submitted!",
        description: "Thank you for your interest. We'll review your application and get back to you soon."
      });

      // Reset form and close dialog
      setFormData({
        fullName: "",
        email: "",
        company: "",
        jobTitle: "",
        bio: "",
        topicTitle: "",
        topicDescription: "",
        linkedinUrl: "",
        portfolioUrl: ""
      });
      onOpenChange(false);
    } catch (error) {
      console.error('Error submitting application:', error);
      toast({
        title: "Submission Failed",
        description: "There was an error submitting your application. Please try again.",
        variant: "destructive"
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Apply to Become a Speaker</DialogTitle>
          <DialogDescription>
            Share your expertise with our community. Fill out the form below to apply.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="fullName">
                Full Name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="fullName"
                value={formData.fullName}
                onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                placeholder="John Doe"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">
                Email <span className="text-destructive">*</span>
              </Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="john@example.com"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="company">Company</Label>
              <Input
                id="company"
                value={formData.company}
                onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                placeholder="Your Company"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="jobTitle">Job Title</Label>
              <Input
                id="jobTitle"
                value={formData.jobTitle}
                onChange={(e) => setFormData({ ...formData, jobTitle: e.target.value })}
                placeholder="Your Role"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="bio">
              Bio / About You <span className="text-destructive">*</span>
            </Label>
            <Textarea
              id="bio"
              value={formData.bio}
              onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
              placeholder="Tell us about yourself, your experience, and expertise..."
              rows={4}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="topicTitle">
              Proposed Talk Title <span className="text-destructive">*</span>
            </Label>
            <Input
              id="topicTitle"
              value={formData.topicTitle}
              onChange={(e) => setFormData({ ...formData, topicTitle: e.target.value })}
              placeholder="The Future of Digital Nomadism"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="topicDescription">
              Talk Description <span className="text-destructive">*</span>
            </Label>
            <Textarea
              id="topicDescription"
              value={formData.topicDescription}
              onChange={(e) => setFormData({ ...formData, topicDescription: e.target.value })}
              placeholder="Describe what you'd like to talk about..."
              rows={3}
              required
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="linkedinUrl">LinkedIn URL</Label>
              <Input
                id="linkedinUrl"
                type="url"
                value={formData.linkedinUrl}
                onChange={(e) => setFormData({ ...formData, linkedinUrl: e.target.value })}
                placeholder="https://linkedin.com/in/yourprofile"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="portfolioUrl">Portfolio/Website URL</Label>
              <Input
                id="portfolioUrl"
                type="url"
                value={formData.portfolioUrl}
                onChange={(e) => setFormData({ ...formData, portfolioUrl: e.target.value })}
                placeholder="https://yourwebsite.com"
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Submit Application
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
